/**
 * Diorama water shade: one occupancy mask of swept visual-rise hulls, filled at
 * alpha 1 so terrace overlaps stay one darkness. Object alpha is strength.
 * Zenith skips the draw — no footprint stamp, no side ghosts.
 *
 * The mask is baked once per sun / map change into a low-resolution world-space
 * `DynamicTexture` and shown as one Image (docs/PERFORMANCE.md, Phase 2). Bilinear
 * upscaling is the penumbra. The previous per-frame Graphics + Blur filter cost
 * ~12 ms of GPU per frame on an integrated GPU; it survives behind `?shadebake=0`
 * for A/B runs only.
 */

import * as Phaser from 'phaser';
import {
  DAY_WATER_ALPHA,
  DAY_WATER_COLOR,
  NIGHT_WATER_ALPHA,
  NIGHT_WATER_COLOR,
} from '../../sim/environment/water.ts';
import {
  castFactor,
  castOffsetPx,
  evaluateSun,
  resolveElevationStack,
  shadeAlpha,
  sweptColumnHull,
  tessellateRing,
  visualRisePx,
  type MapDocument,
  type Ring,
  type Sun,
} from '../../sim/map/index.ts';
import { WATER_FRAGMENT } from './shaders.ts';
import { RENDER_FLAGS } from '../renderFlags.ts';

/** Cool overlay on the water sheet. Multiply lightened the translucent cyan; this darkens it. */
const UMBRA_COLOR = 0x0e2430;
const UMBRA_ALPHA = 0.38;
/**
 * Above the water sheet and the shallow bed. Beds sit under the sheet, so
 * this overlay darkens underwater objects and open water together.
 */
const SHADE_DEPTH = 1.7;
/** Low-quality silhouette soften for the legacy Graphics path (`?shadebake=0`). */
const SHADE_BLUR_QUALITY = 0;
const SHADE_BLUR_OFFSET = 2;
const SHADE_BLUR_STRENGTH = 0.85;
/**
 * Baked mask: one texel covers at least this many world pixels, more on a map wider
 * than `SHADE_TEXTURE_MAX_PX` texels. 4 world px is 3 screen px at race zoom, so the
 * bilinear edge reads like the old 2 px blur.
 */
export const SHADE_TEXEL_WORLD_PX_MIN = 4;
export const SHADE_TEXTURE_MAX_PX = 2048;

function rgb01(hex: number): [number, number, number] {
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
}

function authoredAltitude01(sun: Sun): number {
  return sun.altitude / (Math.PI / 2);
}

/** World pixels per texel for a map of the given bounds. Pure so tests can pin it. */
export function shadeTexelWorldPx(bounds: { width: number; height: number }): number {
  const largest = Math.max(bounds.width, bounds.height, 1);
  return Math.max(SHADE_TEXEL_WORLD_PX_MIN, Math.ceil(largest / SHADE_TEXTURE_MAX_PX));
}

function fillOccupancy(
  gfx: Phaser.GameObjects.Graphics,
  points: Phaser.Math.Vector2[],
  offset: Phaser.Math.Vector2,
): void {
  const hull = sweptColumnHull(points, offset);

  if (hull.length < 3) {
    return;
  }

  gfx.fillPoints(
    hull.map((point) => new Phaser.Math.Vector2(point.x, point.y)),
    true,
    true,
  );
}

export class SunLighting {
  readonly isSupported: boolean;

  private readonly scene: Phaser.Scene;
  private readonly waterKey: string;
  private readonly shadeTextureKey: string;
  private readonly bake: boolean;
  private enabled = false;
  private water: Phaser.GameObjects.Shader | null = null;
  /** Legacy path: the shade Graphics on the display list, with the Blur filter. */
  private shade: Phaser.GameObjects.Graphics | null = null;
  private shadeBlur: Phaser.Filters.Blur | null = null;
  /** Bake path: an off-list Graphics drawn once into the texture the Image shows. */
  private shadeSource: Phaser.GameObjects.Graphics | null = null;
  private shadeTexture: Phaser.Textures.DynamicTexture | null = null;
  private shadeImage: Phaser.GameObjects.Image | null = null;
  private shadeBakeKey = '';
  private sheetOverride: { color: number; alpha: number } | null = null;
  private shadeKey = '';

  constructor(scene: Phaser.Scene, keyPrefix: string) {
    this.scene = scene;
    this.isSupported = scene.renderer.type === Phaser.WEBGL;
    this.waterKey = `${keyPrefix}-water`;
    this.shadeTextureKey = `${keyPrefix}-shade-mask`;
    this.bake = RENDER_FLAGS.shadeBake;
  }

  /** True when the water Shader is the live sheet (WebGL and enabled). */
  get usesShader(): boolean {
    return this.isSupported && this.enabled && this.water !== null;
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  /** True when the shade is a baked texture rather than a per-frame Graphics. */
  get usesBakedShade(): boolean {
    return this.bake;
  }

  setEnabled(
    on: boolean,
    map: MapDocument,
    visibleIds?: ReadonlySet<string> | null,
  ): void {
    this.enabled = on;

    if (!this.isSupported) {
      return;
    }

    if (on) {
      this.rebuild(map, visibleIds);
      this.water?.setVisible(true);
      this.shade?.setVisible(true);
      this.shadeImage?.setVisible(this.shadeImage.alpha > 0);
    } else {
      this.shadeKey = '';
      this.shadeBakeKey = '';
      this.shadeBlur?.setActive(false);
      this.water?.setVisible(false);
      this.shade?.setVisible(false);
      this.shadeImage?.setVisible(false);
    }
  }

  rebuild(map: MapDocument, visibleIds?: ReadonlySet<string> | null): void {
    if (!this.isSupported || !this.enabled) {
      return;
    }

    const sun = evaluateSun(map.environment);
    this.shadeKey = visibleIds ? [...visibleIds].sort().join('\0') : '';
    this.ensureWater(map);

    if (this.bake) {
      this.bakeShade(map, sun);
    } else {
      this.drawShade(map, sun, visibleIds);
    }

    this.applyWaterUniforms(sun);
    this.water?.setVisible(true);
  }

  /**
   * Column visibility changed. The baked mask covers the whole map, so it only builds
   * once; the legacy Graphics path rebuilds its occupancy for the visible set.
   */
  syncVisibleShade(
    map: MapDocument,
    visibleIds: ReadonlySet<string>,
    key: string,
  ): void {
    if (!this.enabled) {
      return;
    }

    if (this.bake) {
      if (this.shadeBakeKey === '') {
        this.rebuild(map, visibleIds);
      }

      return;
    }

    if (key === this.shadeKey) {
      return;
    }

    this.rebuild(map, visibleIds);
  }

  destroy(): void {
    this.shadeBlur?.destroy();
    this.shadeBlur = null;
    this.shade?.destroy();
    this.shade = null;
    this.shadeImage?.destroy();
    this.shadeImage = null;
    this.shadeSource?.destroy();
    this.shadeSource = null;

    if (this.shadeTexture) {
      this.scene.textures.remove(this.shadeTextureKey);
      this.shadeTexture = null;
    }

    this.water?.destroy();
    this.water = null;
    this.shadeKey = '';
    this.shadeBakeKey = '';
  }

  private ensureWater(map: MapDocument): void {
    if (this.water) {
      this.water.setPosition(0, 0);
      this.water.setSize(map.bounds.width, map.bounds.height);
      return;
    }

    this.water = this.scene.add.shader(
      {
        name: this.waterKey,
        fragmentSource: WATER_FRAGMENT,
      },
      0,
      0,
      map.bounds.width,
      map.bounds.height,
    );
    this.water.setOrigin(0, 0);
    this.water.setDepth(1.25);
  }

  // --- Baked shade -----------------------------------------------------------------

  /** Everything the mask depends on: bounds, sun geometry, and every body's outline + height. */
  private bakeKey(map: MapDocument, sun: Sun): string {
    const stack = resolveElevationStack(map);
    const parts: string[] = [
      `${map.bounds.width}x${map.bounds.height}`,
      `${sun.direction.x},${sun.direction.y},${sun.irradiance},${castFactor(authoredAltitude01(sun))}`,
    ];

    for (const body of [...map.islands, ...map.rocks]) {
      const points = tessellateRing(body);
      const first = points[0];
      const elevation = stack.get(body.id)?.worldElevation ?? body.elevation;
      // Outline identity is a stable change detector (see ElevatedBodyGraphicsPool), but a
      // string key must be value based: count, first point, and a bounding sum are enough.
      let sum = 0;

      for (const point of points) {
        sum += point.x * 3 + point.y;
      }

      parts.push(`${body.id}:${elevation}:${points.length}:${first?.x ?? 0},${first?.y ?? 0}:${sum}`);
    }

    return parts.join('|');
  }

  private ensureBakeTargets(map: MapDocument): {
    texture: Phaser.Textures.DynamicTexture;
    image: Phaser.GameObjects.Image;
    source: Phaser.GameObjects.Graphics;
    scale: number;
  } {
    const scale = shadeTexelWorldPx(map.bounds);
    const width = Math.max(2, Math.ceil(map.bounds.width / scale));
    const height = Math.max(2, Math.ceil(map.bounds.height / scale));

    let texture = this.shadeTexture;

    if (!texture) {
      const existing = this.scene.textures.exists(this.shadeTextureKey)
        ? this.scene.textures.get(this.shadeTextureKey)
        : null;

      if (existing instanceof Phaser.Textures.DynamicTexture) {
        texture = existing;
      } else {
        if (existing) {
          this.scene.textures.remove(this.shadeTextureKey);
        }

        const created = this.scene.textures.addDynamicTexture(
          this.shadeTextureKey,
          width,
          height,
        );

        if (!created) {
          throw new Error(`Could not create shade texture ${this.shadeTextureKey}`);
        }

        texture = created;
      }

      this.shadeTexture = texture;
    }

    if (texture.width !== width || texture.height !== height) {
      texture.setSize(width, height);
    }

    // World (0, 0) at texel (0, 0), `scale` world px per texel.
    texture.camera.setOrigin(0, 0);
    texture.camera.setScroll(0, 0);
    texture.camera.setZoom(1 / scale);

    if (!this.shadeSource) {
      // Off the display list: only ever drawn into the texture.
      this.shadeSource = this.scene.make.graphics({ x: 0, y: 0 }, false);
    }

    if (!this.shadeImage) {
      this.shadeImage = this.scene.add
        .image(0, 0, this.shadeTextureKey)
        .setOrigin(0, 0)
        .setDepth(SHADE_DEPTH);
    }

    this.shadeImage.setTexture(this.shadeTextureKey);
    this.shadeImage.setScale(scale);
    this.shadeImage.setPosition(0, 0);

    return { texture, image: this.shadeImage, source: this.shadeSource, scale };
  }

  private bakeShade(map: MapDocument, sun: Sun): void {
    const off = sun.irradiance <= 0 || castFactor(authoredAltitude01(sun)) <= 0;
    const key = off ? 'off' : this.bakeKey(map, sun);

    if (this.shadeImage && key === this.shadeBakeKey) {
      // Strength can change without moving the disc; alpha is cheap to refresh.
      this.shadeImage.setAlpha(off ? 0 : shadeAlpha(UMBRA_ALPHA, sun.strength));
      this.shadeImage.setVisible(!off);
      return;
    }

    this.shadeBakeKey = key;

    if (off) {
      this.shadeImage?.setAlpha(0).setVisible(false);
      return;
    }

    const { texture, image, source } = this.ensureBakeTargets(map);
    source.clear();
    source.fillStyle(UMBRA_COLOR, 1);
    const painted = this.paintOccupancy(source, map, sun, null);

    texture.clear();

    if (painted) {
      texture.draw(source);
    }

    texture.render();
    image.setAlpha(painted ? shadeAlpha(UMBRA_ALPHA, sun.strength) : 0);
    image.setVisible(painted);
  }

  /** Swept hull per body at alpha 1. Returns false when nothing casts. */
  private paintOccupancy(
    gfx: Phaser.GameObjects.Graphics,
    map: MapDocument,
    sun: Sun,
    visibleIds: ReadonlySet<string> | null,
  ): boolean {
    let painted = false;

    const paintRing = (ring: Ring, elevation: number): void => {
      if (elevation <= 0) {
        return;
      }

      const points = tessellateRing(ring).map(
        (point) => new Phaser.Math.Vector2(point.x, point.y),
      );

      if (points.length < 3) {
        return;
      }

      const offsetPx = castOffsetPx(visualRisePx(elevation), sun);

      if (offsetPx.x === 0 && offsetPx.y === 0) {
        return;
      }

      fillOccupancy(gfx, points, new Phaser.Math.Vector2(offsetPx.x, offsetPx.y));
      painted = true;
    };

    const stack = resolveElevationStack(map);
    const include = (id: string): boolean => !visibleIds || visibleIds.has(id);

    for (const island of map.islands) {
      if (include(island.id)) {
        paintRing(island, stack.get(island.id)?.worldElevation ?? island.elevation);
      }
    }

    for (const rock of map.rocks) {
      if (include(rock.id)) {
        paintRing(rock, stack.get(rock.id)?.worldElevation ?? rock.elevation);
      }
    }

    return painted;
  }

  // --- Legacy per-frame shade (`?shadebake=0`) -------------------------------------

  private ensureShade(): Phaser.GameObjects.Graphics {
    if (this.shade) {
      return this.shade;
    }

    const gfx = this.scene.add.graphics();
    gfx.setDepth(SHADE_DEPTH);

    if (this.scene.renderer.type === Phaser.WEBGL) {
      gfx.enableFilters();
      this.shadeBlur =
        gfx.filters?.internal.addBlur(
          SHADE_BLUR_QUALITY,
          SHADE_BLUR_OFFSET,
          SHADE_BLUR_OFFSET,
          SHADE_BLUR_STRENGTH,
        ) ?? null;
    }

    this.shade = gfx;
    return gfx;
  }

  private drawShade(
    map: MapDocument,
    sun: Sun,
    visibleIds?: ReadonlySet<string> | null,
  ): void {
    const gfx = this.ensureShade();
    gfx.clear();
    gfx.setAlpha(1);

    if (sun.irradiance <= 0 || castFactor(authoredAltitude01(sun)) <= 0) {
      this.shadeBlur?.setActive(false);
      gfx.setAlpha(0);
      return;
    }

    gfx.fillStyle(UMBRA_COLOR, 1);
    const painted = this.paintOccupancy(gfx, map, sun, visibleIds ?? null);

    if (!painted) {
      this.shadeBlur?.setActive(false);
      gfx.setAlpha(0);
      return;
    }

    gfx.setAlpha(shadeAlpha(UMBRA_ALPHA, sun.strength));
    this.shadeBlur?.setActive(true);
    gfx.setVisible(true);
  }

  // --- Water sheet ---------------------------------------------------------------

  /**
   * Debug sheet tint. Day and night uniforms take the same values so the daylight mix is a
   * no-op. Stored so a later `rebuild` does not snap back to the sky lerp.
   */
  setSheetAppearance(color: number, alpha: number): void {
    this.sheetOverride = { color, alpha };
    this.applySheetUniforms();
  }

  private applyWaterUniforms(sun: Sun): void {
    if (this.sheetOverride) {
      this.applySheetUniforms();
      return;
    }

    const water = this.water;

    if (!water) {
      return;
    }

    water.setUniform('uDayColor', rgb01(DAY_WATER_COLOR));
    water.setUniform('uNightColor', rgb01(NIGHT_WATER_COLOR));
    water.setUniform('uDayAlpha', DAY_WATER_ALPHA);
    water.setUniform('uNightAlpha', NIGHT_WATER_ALPHA);
    water.setUniform('uDaylight', sun.skyLight);
  }

  private applySheetUniforms(): void {
    const water = this.water;
    const override = this.sheetOverride;

    if (!water || !override) {
      return;
    }

    const rgb = rgb01(override.color);

    water.setUniform('uDayColor', rgb);
    water.setUniform('uNightColor', rgb);
    water.setUniform('uDayAlpha', override.alpha);
    water.setUniform('uNightAlpha', override.alpha);
    water.setUniform('uDaylight', 1);
  }
}
